const ICE_CANDIDATE_RE = /candidate:\S+\s+\d+\s+\S+\s+\d+\s+(\S+)\s+\d+\s+typ\s+(\S+)/i;

export function isIpv6Address(value) {
  return String(value || '').includes(':');
}

export function isPrivateOrLocalIp(value) {
  const ip = String(value || '').trim().split('%')[0];
  if (!ip) return false;
  if (ip === '::1' || ip === '0.0.0.0' || ip === '::') return true;
  if (/^127\./.test(ip) || /^10\./.test(ip) || /^192\.168\./.test(ip)) return true;
  if (/^172\.(1[6-9]|2\d|3[0-1])\./.test(ip)) return true;
  if (/^169\.254\./.test(ip) || /^fe80:/i.test(ip) || /^f[cd]/i.test(ip)) return true;
  return false;
}

export function isPublicIp(value) {
  const ip = String(value || '').trim().split('%')[0];
  if (!ip || ip.includes('.local')) return false;
  if (!/^\d{1,3}(\.\d{1,3}){3}$/.test(ip) && !ip.includes(':')) return false;
  return !isPrivateOrLocalIp(ip);
}

export function parseIceCandidate(raw) {
  if (raw && typeof raw === 'object') {
    const ip = String(raw.ip || raw.address || '').trim().split('%')[0];
    if (!ip) return null;
    return { ip, type: String(raw.type || raw.typ || 'host').toLowerCase() };
  }
  const match = ICE_CANDIDATE_RE.exec(String(raw || ''));
  if (!match) return null;
  return { ip: match[1].split('%')[0], type: String(match[2] || 'host').toLowerCase() };
}

export function summarizeWebRtcCandidates(candidates = []) {
  const publicIps = [];
  const privateIps = [];
  const ipv6Ips = [];
  const seen = new Set();
  candidates.forEach((item) => {
    const parsed = parseIceCandidate(item);
    const ip = parsed?.ip;
    if (!ip || seen.has(ip) || ip.includes('.local')) return;
    seen.add(ip);
    if (isIpv6Address(ip)) ipv6Ips.push(ip);
    if (isPublicIp(ip)) publicIps.push(ip);
    else if (isPrivateOrLocalIp(ip)) privateIps.push(ip);
  });
  return { candidates: [...candidates], publicIps, privateIps, ipv6Ips };
}

export function collectBrowserSignals() {
  const screenObj = typeof window !== 'undefined' ? window.screen : null;
  return {
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || '',
    locale: typeof navigator !== 'undefined' ? navigator.language : '',
    languages: typeof navigator !== 'undefined' ? [...(navigator.languages || [])] : [],
    screen: {
      width: screenObj?.width || 0,
      height: screenObj?.height || 0,
      availWidth: screenObj?.availWidth || 0,
      availHeight: screenObj?.availHeight || 0,
      colorDepth: screenObj?.colorDepth || 0,
    },
    viewport: {
      width: typeof window !== 'undefined' ? window.innerWidth : 0,
      height: typeof window !== 'undefined' ? window.innerHeight : 0,
    },
    outerWindow: {
      width: typeof window !== 'undefined' ? window.outerWidth : 0,
      height: typeof window !== 'undefined' ? window.outerHeight : 0,
    },
    devicePixelRatio: typeof window !== 'undefined' ? window.devicePixelRatio : 1,
    userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : '',
    platform: typeof navigator !== 'undefined' ? navigator.platform : '',
    hardwareConcurrency: typeof navigator !== 'undefined' ? navigator.hardwareConcurrency : null,
    maxTouchPoints: typeof navigator !== 'undefined' ? navigator.maxTouchPoints : 0,
  };
}

export async function collectWebRtcSignals({ waitMs = 1500 } = {}) {
  if (typeof RTCPeerConnection !== 'function') {
    return {
      candidates: [],
      publicIps: [],
      privateIps: [],
      ipv6Ips: [],
      error: 'webrtc_unavailable',
    };
  }
  const candidates = [];
  const pc = new RTCPeerConnection({ iceServers: [] });
  try {
    pc.createDataChannel('qa-diagnostics');
    const done = new Promise((resolve) => {
      const finish = () => resolve();
      pc.addEventListener('icecandidate', (event) => {
        if (!event?.candidate?.candidate) {
          finish();
          return;
        }
        candidates.push(event.candidate.candidate);
      });
      pc.addEventListener('icegatheringstatechange', () => {
        if (pc.iceGatheringState === 'complete') finish();
      });
      window.setTimeout(finish, waitMs);
    });
    const offer = await pc.createOffer();
    await pc.setLocalDescription(offer);
    await done;
  } catch {
    return {
      ...summarizeWebRtcCandidates(candidates),
      error: 'webrtc_gather_failed',
    };
  } finally {
    try {
      pc.close();
    } catch {
      /* ignore */
    }
  }
  return summarizeWebRtcCandidates(candidates);
}

export async function collectQaDiagnosticSignals() {
  const browser = collectBrowserSignals();
  const webrtc = await collectWebRtcSignals();
  return { ...browser, webrtc };
}
